import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";

export default function PaymentReturn({ type }) {
  const [state, setState] = useState(type === "success" ? "loading" : "cancelled");
  const location = useLocation();
  const booking = JSON.parse(localStorage.getItem("ip_last_booking") || "null");

  useEffect(() => {
    if (type !== "success") return;
    const sid = new URLSearchParams(location.search).get("session_id");
    if (!sid) {
      setState("failed");
      return;
    }
    let tries = 0;
    const timer = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState("paid");
          localStorage.removeItem("ip_last_booking");
          clearInterval(timer);
        }
      } catch (e) {
        /* retry au prochain tick */
      }
      if (tries >= 15) {
        setState("pending");
        clearInterval(timer);
      }
    }, 2000);
    return () => clearInterval(timer);
  }, [type, location.search]);

  return (
    <div className="cyber-grid flex min-h-screen items-center justify-center bg-void px-5">
      <motion.div
        data-testid="payment-return-panel"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="glass-panel w-full max-w-lg p-10 text-center"
      >
        {state === "loading" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-cyan-400" />
            <h1 className="mt-5 font-display text-2xl font-bold uppercase text-slate-100">
              Vérification du paiement…
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Confirmation en cours auprès de Stripe, quelques secondes.
            </p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
            <h1 className="mt-5 font-display text-2xl font-bold uppercase text-slate-100">
              Paiement confirmé
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Votre session est payée et confirmée. À très vite dans l'arène !
            </p>
            {booking && (
              <div className="mx-auto mt-7 inline-block border border-emerald-500/40 bg-emerald-500/10 px-8 py-4">
                <span className="font-orbitron text-2xl font-black tracking-[0.15em] text-emerald-300">
                  {booking.reference}
                </span>
                <p className="mt-1 font-hud text-[11px] uppercase tracking-wider text-slate-400">
                  {booking.experience} · {booking.date} à {booking.time_slot} · {booking.total_price}€
                </p>
              </div>
            )}
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 text-amber-400" />
            <h1 className="mt-5 font-display text-2xl font-bold uppercase text-slate-100">
              Paiement en cours de traitement
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Stripe n'a pas encore confirmé. Votre réservation reste enregistrée ;
              vérifiez dans quelques instants ou contactez-nous.
            </p>
          </>
        )}
        {(state === "cancelled" || state === "failed") && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-400" />
            <h1 className="mt-5 font-display text-2xl font-bold uppercase text-slate-100">
              Paiement annulé
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Aucun montant débité. Votre réservation reste valable : vous pouvez
              payer sur place le jour J, ou réessayer le paiement en ligne.
            </p>
          </>
        )}
        <Link
          data-testid="payment-back-home"
          to="/"
          className="mt-9 inline-block border border-cyan-500/40 px-8 py-3 font-hud text-xs font-bold uppercase tracking-[0.2em] text-cyan-300 transition-all duration-300 hover:bg-cyan-400 hover:text-void"
        >
          Retour au site
        </Link>
      </motion.div>
    </div>
  );
}
